/**
 * Maps backend DynamoDB timestamp fields to frontend field names.
 *
 * Backend uses `created` and `lastModified`.
 * Frontend types use `createdAt` and `updatedAt`.
 */
export type TimestampMapped<T extends Record<string, unknown>> =
  Omit<T, 'created' | 'lastModified'>
  & ('created' extends keyof T
    ? (undefined extends T['created']
      ? { createdAt?: Exclude<T['created'], undefined> }
      : { createdAt: T['created'] })
    : Record<never, never>)
  & ('lastModified' extends keyof T
    ? (undefined extends T['lastModified']
      ? { updatedAt?: Exclude<T['lastModified'], undefined> }
      : { updatedAt: T['lastModified'] })
    : Record<never, never>);

export function mapTimestamps<T extends Record<string, unknown>>(item: T): TimestampMapped<T> {
  const mapped = { ...item } as Record<string, unknown>;

  if ('created' in mapped) {
    mapped.createdAt = mapped.created;
    delete mapped.created;
  }

  if ('lastModified' in mapped) {
    mapped.updatedAt = mapped.lastModified;
    delete mapped.lastModified;
  }

  return mapped as TimestampMapped<T>;
}

export function mapTimestampsArray<T extends Record<string, unknown>>(
  items: T[],
): TimestampMapped<T>[] {
  return items.map(mapTimestamps);
}
